import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Plus, Search, Truck, Building2, Warehouse } from "lucide-react";
import { toast } from "sonner";

const emptyEmp = () => ({
  first_name: "", last_name: "", email: "", phone: "", department: "", title: "",
  employee_type: "office", hire_date: "", nationality: "TR", iban: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  license_class: "", license_expiry: "", src_type: "", src_expiry: "",
  adr_class: "", adr_expiry: "", psychotechnical_expiry: "",
  tachograph_card_no: "", tachograph_expiry: "",
  passport_no: "", passport_expiry: "", visa_country: "", visa_expiry: "",
  forklift_license: "", forklift_expiry: "", health_report_expiry: "",
  salary: 0, currency: "TRY", status: "active",
});

export default function Employees() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyEmp());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (filter !== "all") params.employee_type = filter;
    const { data } = await api.get("/employees", { params });
    setRows(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const create = async () => {
    setSaving(true);
    try {
      const payload = { ...form, salary: parseFloat(form.salary) || 0 };
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      payload.employee_type = form.employee_type;
      payload.status = form.status;
      payload.currency = form.currency;
      await api.post("/employees", payload);
      toast.success(t("saved"));
      setOpen(false);
      setForm(emptyEmp());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    }
    setSaving(false);
  };

  const typeIcon = (t) => t === "driver" ? Truck : t === "warehouse" ? Warehouse : Building2;

  return (
    <div className="space-y-5" data-testid="employees-page">
      {/* Filters */}
      <Card className="p-4 border-border/60 flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="input-emp-search"
            placeholder={t("search")}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {["all", "office", "driver", "warehouse"].map(f => (
            <Button
              key={f}
              data-testid={`filter-type-${f}`}
              variant={filter === f ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f)}
            >
              {t(f === "all" ? "all" : f)}
            </Button>
          ))}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="btn-add-employee" size="sm" className="ml-2"><Plus className="w-4 h-4 mr-1" /> {t("newEmployee")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{t("newEmployee")}</DialogTitle></DialogHeader>
              <EmployeeForm form={form} setForm={setForm} />
              <DialogFooter>
                <Button data-testid="btn-save-employee" onClick={create} disabled={saving}>{t("save")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </Card>

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((e, idx) => {
          const Icon = typeIcon(e.employee_type);
          const initials = `${e.first_name?.[0] || ""}${e.last_name?.[0] || ""}`.toUpperCase();
          return (
            <Link key={e.id} to={`/employees/${e.id}`} data-testid={`emp-card-${idx}`} className="block">
              <Card className="p-5 border-border/60 card-hover h-full">
                <div className="flex items-start gap-3">
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-primary/15 text-primary font-semibold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs font-mono-brand uppercase tracking-wider text-muted-foreground">
                      <Icon className="w-3 h-3" />
                      <span>{e.employee_no}</span>
                    </div>
                    <div className="font-display font-bold truncate">{e.first_name} {e.last_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{e.title || "—"}</div>
                  </div>
                  <Badge variant={e.status === "active" ? "default" : "secondary"} className="shrink-0">{t(e.status)}</Badge>
                </div>
                <div className="mt-3 pt-3 border-t border-border/60 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">{t("department")}</div>
                    <div className="font-medium truncate">{e.department || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">{t("employeeType")}</div>
                    <div className="font-medium">{t(e.employee_type)}</div>
                  </div>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>
      {rows.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground border-border/60">{t("noData")}</Card>
      )}
    </div>
  );
}

export function EmployeeForm({ form, setForm }) {
  const { t } = useI18n();
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setV = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const Field = ({ k, type = "text", label }) => (
    <div className="space-y-1">
      <Label className="text-xs">{label || t(k)}</Label>
      <Input data-testid={`field-${k}`} type={type} value={form[k] || ""} onChange={set(k)} />
    </div>
  );

  return (
    <div className="space-y-5">
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("personalInfo")}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field k="first_name" label={t("firstName")} />
          <Field k="last_name" label={t("lastName")} />
          <Field k="email" type="email" label={t("email")} />
          <Field k="phone" label={t("phone")} />
          <Field k="national_id" label={t("nationalId")} />
          <Field k="nationality" label={t("nationality")} />
          <Field k="birth_date" type="date" label={t("birthDate")} />
          <Field k="hire_date" type="date" label={t("hireDate")} />
          <div className="md:col-span-2"><Field k="address" label={t("address")} /></div>
          <Field k="department" label={t("department")} />
          <Field k="title" label={t("title")} />
          <div className="space-y-1">
            <Label className="text-xs">{t("employeeType")}</Label>
            <Select value={form.employee_type} onValueChange={(v) => setV("employee_type", v)}>
              <SelectTrigger data-testid="field-employee_type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="office">{t("office")}</SelectItem>
                <SelectItem value="driver">{t("driver")}</SelectItem>
                <SelectItem value="warehouse">{t("warehouse")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">{t("status")}</Label>
            <Select value={form.status} onValueChange={(v) => setV("status", v)}>
              <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">{t("active")}</SelectItem>
                <SelectItem value="onleave">{t("onleave")}</SelectItem>
                <SelectItem value="terminated">{t("terminated")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Field k="emergency_contact_name" label={t("emergencyContact")} />
          <Field k="emergency_contact_phone" label={t("emergencyPhone")} />
        </div>
      </section>

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("financial")}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field k="iban" label={t("iban")} />
          <Field k="bank_name" label={t("bankName")} />
          <Field k="salary" type="number" label={t("salary")} />
        </div>
      </section>

      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{t("logistics")}</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field k="license_class" label={`${t("license")} ${t("category")}`} />
          <Field k="license_no" label={`${t("license")} No`} />
          <Field k="license_expiry" type="date" label={`${t("license")} ${t("expiryDate")}`} />
          <Field k="src_type" label="SRC" />
          <Field k="src_expiry" type="date" label={`SRC ${t("expiryDate")}`} />
          <Field k="adr_class" label="ADR" />
          <Field k="adr_expiry" type="date" label={`ADR ${t("expiryDate")}`} />
          <Field k="psychotechnical_expiry" type="date" label={t("psychotechnical")} />
          <Field k="tachograph_card_no" label={`${t("tachograph")} No`} />
          <Field k="tachograph_expiry" type="date" label={`${t("tachograph")} ${t("expiryDate")}`} />
          <Field k="passport_no" label={t("passport")} />
          <Field k="passport_expiry" type="date" label={`${t("passport")} ${t("expiryDate")}`} />
          <Field k="visa_country" label={t("visa")} />
          <Field k="visa_expiry" type="date" label={`${t("visa")} ${t("expiryDate")}`} />
          <Field k="forklift_expiry" type="date" label={`${t("forklift")} ${t("expiryDate")}`} />
          <Field k="health_report_expiry" type="date" label={t("healthReport")} />
        </div>
      </section>
    </div>
  );
}
